import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AnalyticsKPIs {
  total_tasks: number;
  on_time_pct: number;
  late_count: number;
  incidents: number;
  vital_alerts: number;
}

export interface DailyActivity {
  date: string;
  on_time: number;
  late: number;
}

export interface CaregiverRank {
  user_id: string;
  name: string;
  avatar_url: string | null;
  total_tasks: number;
  on_time_pct: number | null;
  incidents: number;
  shifts_count: number;
  checkin_on_time_pct: number | null;
  last_shift_date: string | null;
  score: number;
}

export interface AttentionItem {
  user_id: string;
  name: string;
  avatar_url: string | null;
  reasons: ('low_punctuality' | 'high_incidents' | 'inactive' | 'vital_trend')[];
  total_tasks: number;
  on_time_pct: number | null;
  incidents: number;
  // For patient-level attention items (vital trends)
  patient_id?: string;
  patient_nickname?: string;
  vital_detail?: string;
}

export interface PatientSummaryItem {
  patient_id: string;
  nickname: string;
  photo_url: string | null;
  scheduled: number;
  completed: number;
  completion_pct: number;
  executions: number;
  alerts: number;
  incidents: number;
}

export interface CompanyAnalyticsData {
  kpis: AnalyticsKPIs;
  daily_activity: DailyActivity[];
  caregiver_ranking: CaregiverRank[];
  attention_list: AttentionItem[];
  patient_summary: PatientSummaryItem[];
}

export function useCompanyAnalytics(companyId: string | undefined, periodDays: number) {
  return useQuery({
    queryKey: ['company-analytics', companyId, periodDays],
    queryFn: async (): Promise<CompanyAnalyticsData> => {
      const { data, error } = await supabase.functions.invoke('generate-company-analytics', {
        body: { company_id: companyId, period_days: periodDays },
      });

      if (error) throw error;
      return data as CompanyAnalyticsData;
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });
}
